import { useMemo } from 'react';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';

import { type FieldMatchingFileColumn } from '@/api/domains/field-matching.types';
import { type PaginatedList } from '@/api/types/pagination.types';

import { type FieldMatchingFormValues } from './types';

// ----------------------------------------------------------------------

type UseFieldMatchingOptionsParams = Pick<UseQueryOptions, 'queryKey'> & {
  fetchFn: () => Promise<PaginatedList<'items', FieldMatchingFileColumn>>;
};

const defaultItems: string[] = [];
const defaultTitles: Record<string, string> = {};

export const useFieldMatchingOptions = (params: UseFieldMatchingOptionsParams) => {
  const { queryKey, fetchFn } = params;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const { items } = await fetchFn();

      const titleByKey: Record<string, string> = {};
      const keys: string[] = [];

      items.forEach(({ key, title }) => {
        titleByKey[key] = title;
        keys.push(key);
      });

      return { titleByKey, keys };
    },
    staleTime: Infinity,
    retry: false,
  });

  const { data } = query;

  const keys = data?.keys ?? defaultItems;
  const titleByKey = data?.titleByKey ?? defaultTitles;

  const { columns: selectedColumns } = useWatch<FieldMatchingFormValues>();

  const { selectedOptions, options } = useMemo(() => {
    const selectedOptions = selectedColumns ? Object.values(selectedColumns) : [];

    // Sort the options by the order of the selected options
    const options = [...keys].sort((a, b) => selectedOptions.indexOf(a) - selectedOptions.indexOf(b));

    return { selectedOptions, options };
  }, [selectedColumns, keys]);

  return { query, titleByKey, selectedOptions, options };
};
